import React, { useState, useEffect, useRef } from 'react';
import { Body, Header, Main } from '../../NecttosComp/Layout/Layout';
import Button from '../../NecttosComp/Button/Button';
import { getAxiosTokenInstance } from '../../utils/axiosInstance';
import toast from 'react-hot-toast';
import { getMenuList } from '../windows/MenuList';
import { Table, Tbody, Td, Th, Thead, Tr, useSortableData } from '../../NecttosComp/Table/Table';
import Input from '../../NecttosComp/Input/Input';
import Input2 from '../../NecttosComp/Input/Input2';

interface DocumentationProps {
	_id: string;
	onClose: () => void;
}

const Documentation = ({ onClose }: DocumentationProps) => {
	const [formData, setFormData] = useState<{
		_id: string;
		key: string;
		title: string;
		questions: {
			videoLink: string;
			question: string;
			steps: {
				step: string;
				substeps: {
					text: string;
					subpoints: string[];
				}[];
			}[];
		}[];
	}>({
		_id: '',
		key: '',
		title: '',
		questions: [
			{
				question: '',
				videoLink: '',
				steps: [
					{
						step: '',
						substeps: [
							{
								text: '',
								subpoints: [],
							},
						],
					},
				],
			},
		],
	});

	const [showForm, setShowForm] = useState(false);
	const [documentList, setDocumentList] = useState<any[]>([]);
	const menuList = getMenuList(null, null, null, null, false);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
	const [selectedTitle, setSelectedTitle] = useState('');
	const componentRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLTableElement>(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [docToDelete, setDocToDelete] = useState<string | null>(null);
	const [search, setSearch] = useState('');

	const fetchDocumentation = async () => {
		try {
			const instance = await getAxiosTokenInstance();
			const response = await instance.get('/college/office/documentation/');
			if (response.data?.data) {
				setDocumentList(response.data.data);
			} else {
				toast.error('Failed to load documentation');
			}
		} catch (err) {
			console.error('Error fetching documentation:', err);
			toast.error('Error loading documentation');
		}
	};
	useEffect(() => {
		fetchDocumentation();
	}, []);

	const handleQuestionChange = (index: number, value: string) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[index].question = value;
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const handleStepChange = (questionIndex: number, stepIndex: number, value: string) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].step = value;
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const handleSubstepChange = (questionIndex: number, stepIndex: number, substepIndex: number, value: string) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].substeps[substepIndex].text = value;
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const handleSubpointChange = (questionIndex: number, stepIndex: number, substepIndex: number, subpointIndex: number, value: string) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].substeps[substepIndex].subpoints[subpointIndex] = value;
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const addQuestion = () => {
		setFormData({
			...formData,
			questions: [...(formData.questions ?? []), { question: '', videoLink: '', steps: [{ step: '', substeps: [{ text: '', subpoints: [''] }] }] }],
		});
	};

	const addStep = (questionIndex: number) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps.push({ step: '', substeps: [{ text: '', subpoints: [''] }] });
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const addSubstep = (questionIndex: number, stepIndex: number) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].substeps.push({ text: '', subpoints: [] });
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const addSubpoint = (questionIndex: number, stepIndex: number, substepIndex: number) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].substeps[substepIndex].subpoints.push('');
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const deleteQuestion = (questionIndex: number) => {
		const updatedQuestions = formData.questions.filter((_, i) => i !== questionIndex);
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const deleteStep = (questionIndex: number, stepIndex: number) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps = updatedQuestions[questionIndex].steps.filter((_, i) => i !== stepIndex);
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const deleteSubstep = (questionIndex: number, stepIndex: number, substepIndex: number) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].substeps = updatedQuestions[questionIndex].steps[stepIndex].substeps.filter((_, i) => i !== substepIndex);
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const deleteSubpoint = (questionIndex: number, stepIndex: number, substepIndex: number, subpointIndex: number) => {
		const updatedQuestions = [...formData.questions];
		updatedQuestions[questionIndex].steps[stepIndex].substeps[substepIndex].subpoints = updatedQuestions[questionIndex].steps[stepIndex].substeps[substepIndex].subpoints.filter((_, i) => i !== subpointIndex);
		setFormData({ ...formData, questions: updatedQuestions });
	};

	const handleSubmit = async () => {
		try {
			console.log('Submitting formData with _id:', formData._id);
			const instance = await getAxiosTokenInstance();
			const response = formData._id ? await instance.put(`/college/office/documentation/update/${formData._id}`, formData) : await instance.post('/college/office/documentation/submit', formData);

			if (response.data?.message?.includes('success')) {
				toast.success(`Documentation ${formData._id ? 'updated' : 'submitted'}!`);
				setFormData({
					_id: '',
					key: '',
					title: '',
					questions: [
						{
							videoLink: '',
							question: '',
							steps: [
								{
									step: '',
									substeps: [
										{
											text: '',
											subpoints: [],
										},
									],
								},
							],
						},
					],
				});
				setShowForm(false);
				setSelectedDoc(null);
				fetchDocumentation();
			} else {
				toast.error('Submission failed!');
			}
		} catch (err) {
			console.error('Error submitting documentation:', err);
			toast.error('Error submitting documentation');
		}
	};

	const handleDelete = async (id: string) => {
		try {
			const instance = await getAxiosTokenInstance();
			const res = await instance.delete(`/college/office/documentation/delete/${id}`);

			if (res.data?.message?.includes('deleted')) {
				fetchDocumentation();
			}
		} catch (err) {
			console.error('Error deleting documentation:', err);
		}
	};

	const filteredDocuments = (documentList ?? []).filter((doc) => {
		const selectedMenu = menuList.find((item) => item.keyData === doc.menuKey);
		const titleMatch = doc.title?.toLowerCase().includes(search.toLowerCase());
		const selectedMatch = selectedTitle ? selectedMenu?.title === selectedTitle : true;
		return titleMatch && selectedMatch;
	});

	const { tableItems, requestSort, getIcon } = useSortableData(filteredDocuments);

	return (
		<Main title='Documentation Submission' width='100%' height='100%'>
			{!showForm && (
				<Header>
					<div className='flex gap-2 items-center'>
						<Button type='save' onClick={() => setShowForm(true)}>
							Add
						</Button>

						<Input2 type='text' fieldName='Search' className='w-[300px] h-[40px] border-0' state={search} setState={setSearch} />
					</div>

					<div className='flex'>
						<Button type='print' pdfId='table' fileName='table' tableRef={tableRef.current}>
							Print
						</Button>
						<Button type='excel' tableRef={tableRef.current}>
							Excel
						</Button>
						<Button type='close' onClick={onClose}>
							Close
						</Button>
					</div>
				</Header>
			)}

			<Body>
				{showForm ? (
					<div>
						<div className='flex justify-center items-center h-full'>
							<div className='w-[95%] p-8 bg-[rgba(255,255,255,0.7)] dark:bg-[#18181b] rounded-xl shadow-lg mt-4'>
								<h2 className='text-2xl font-bold mb-4 dark:text-white'>Submit Documentation</h2>
								<div className='grid grid-cols-3 gap-4 mb-4'>
									<Input
										width='100%'
										type='drop'
										fieldName='Title'
										options={menuList}
										optionDisplay='title'
										optionKey='title'
										returnKey='title'
										state={formData}
										setState={(newFormData) => {
											const selected = menuList.find((item) => item.title === newFormData.title);
											setFormData({
												...formData,
												title: newFormData.title,
												key: selected?.keyData || '',
											});
										}}
									/>
								</div>

								<div className='mt-6'>
									<label className='font-semibold text-lg dark:text-white'>Questions</label>
									{(formData.questions ?? []).map((questionObj, questionIndex) => (
										<div key={questionIndex} className='border dark:border-gray-700 p-4 h-full w-full rounded mb-4 bg-gray-50 dark:bg-[#23232a]'>
											<div className='flex items-center gap-2 mb-2'>
												<span className='font-semibold w-25 dark:text-white'>Question {questionIndex + 1}:</span>
												<input className='w-full border dark:border-gray-700 p-2 rounded dark:bg-[#18181b] dark:text-white' value={questionObj.question} onChange={(e) => handleQuestionChange(questionIndex, e.target.value)} placeholder={`Enter question ${questionIndex + 1}`} required />
												<Button type='delete' onClick={() => deleteQuestion(questionIndex)}>
													Delete
												</Button>
											</div>

											<div className='flex items-center gap-2 mb-4'>
												<label className='font-medium text-sm text-gray-700 dark:text-gray-300 w-25'>Video Link:</label>
												<input
													className='w-full border dark:border-gray-700 p-2 rounded dark:bg-[#18181b] dark:text-white'
													value={questionObj.videoLink}
													onChange={(e) => {
														const updatedQuestions = [...formData.questions];
														updatedQuestions[questionIndex].videoLink = e.target.value;
														setFormData({ ...formData, questions: updatedQuestions });
													}}
													placeholder='Video Link'
												/>
											</div>

											<div className='ml-6'>
												<label className='font-medium dark:text-white'>Steps:</label>
												{questionObj.steps.map((step, stepIndex) => (
													<div key={stepIndex} className='flex flex-col gap-2 mt-2 bg-[rgba(255,255,255,0.7)] dark:bg-[#23232a] p-2 rounded shadow-sm'>
														<div className='flex items-center gap-2'>
															<span className='w-24 text-sm text-gray-600 dark:text-gray-300'>Step {stepIndex + 1}:</span>
															<input className='w-full border dark:border-gray-700 p-2 rounded dark:bg-[#18181b] dark:text-white' value={step.step} onChange={(e) => handleStepChange(questionIndex, stepIndex, e.target.value)} placeholder={`Enter step ${stepIndex + 1}`} />
															<Button type='delete' onClick={() => deleteStep(questionIndex, stepIndex)} >
																Delete
															</Button>
														</div>
														<div className='ml-32'>
															<label className='font-medium text-sm text-gray-700 dark:text-gray-300'>Substeps:</label>
															{step.substeps.map((substep, substepIndex) => (
																<div key={substepIndex} className='flex flex-col gap-2 mt-1 w-full'>
																	<div className='flex items-center gap-2'>
																		<input className='w-full border dark:border-gray-700 p-2 rounded dark:bg-[#18181b] dark:text-white' value={substep.text} onChange={(e) => handleSubstepChange(questionIndex, stepIndex, substepIndex, e.target.value)} placeholder={`Substep ${substepIndex + 1}`} />
																		<Button type='delete' onClick={() => deleteSubstep(questionIndex, stepIndex, substepIndex)} >
																			Delete
																		</Button>
																	</div>
																	<div className='ml-6'>
																		<label className='font-medium text-sm text-gray-700 dark:text-gray-300'>Subpoints:</label>
																		{substep.subpoints.map((subpoint, subpointIndex) => (
																			<div key={subpointIndex} className='flex items-center gap-2 mt-1'>
																				<input className='w-full border dark:border-gray-700 p-2 rounded dark:bg-[#18181b] dark:text-white' value={subpoint} onChange={(e) => handleSubpointChange(questionIndex, stepIndex, substepIndex, subpointIndex, e.target.value)} placeholder={`Subpoint ${subpointIndex + 1}`} />
																				<Button type='delete' onClick={() => deleteSubpoint(questionIndex, stepIndex, substepIndex, subpointIndex)}>
																					Delete
																				</Button>
																			</div>
																		))}
																		<div className='flex justify-center mt-2'>
																			<Button type='refresh' onClick={() => addSubpoint(questionIndex, stepIndex, substepIndex)} >
																				+ Add Subpoint
																			</Button>
																		</div>
																	</div>
																</div>
															))}
															<div className='flex justify-start mt-2'>
																<Button type='refresh' onClick={() => addSubstep(questionIndex, stepIndex)}>
																	+ Add Substep
																</Button>
															</div>
														</div>
													</div>
												))}

												<Button type='refresh' onClick={() => addStep(questionIndex)}>
													+ Add Step
												</Button>
											</div>
										</div>
									))}
									<Button type='refresh' onClick={addQuestion}>
										+ Add Question
									</Button>
								</div>

								<div className='flex justify-end mt-6 gap-4'>
									<Button type='close' onClick={() => setShowForm(false)}>
										Close
									</Button>
									<Button type='submit' onClick={handleSubmit}>
										Submit
									</Button>
								</div>
							</div>
						</div>
					</div>
				) : (
					<div
						// ref={tableRef}
						// id='table'
						className='p-6'>
						<h2 className='text-xl font-semibold mb-4 dark:text-white'>Documentation List</h2>
						<Table ref={tableRef} width='100%' innerWidth={['2%', '30%', '20%', '10%']}>
							<Thead>
								<Tr>
									<Th position={0}>Sl. No</Th>
									<Th position={1} onClick={() => requestSort('title')} icon={getIcon('title')}>
										Title
									</Th>
									<Th position={2}>Date Created</Th>
									<Th position={3}>Actions</Th>
								</Tr>
							</Thead>
							<Tbody height='auto'>
								{tableItems.map((doc, i) => {
									const selectedMenu = menuList.find((item) => item.keyData === doc.menuKey);
									return (
										<Tr
											key={i}
											onClick={() => {
												setSelectedDoc(doc);
												setShowDetailModal(true);
											}}>
											<Td index={i} position={0}>
												{i + 1}
											</Td>
											<Td index={i} position={1}>
												{doc.title} {selectedMenu ? ` / ${selectedMenu.classification}` : ''}
											</Td>
											<Td index={i} position={2}>
												{new Date(doc.createdAt).toLocaleDateString()}
											</Td>
											<Td index={i} position={3}>
												<Button
													type='delete'
													onClick={(e) => {
														e.stopPropagation();
														setDocToDelete(doc._id);
														setShowDeleteModal(true);
													}}>
													Delete
												</Button>
											</Td>
										</Tr>
									);
								})}
							</Tbody>
						</Table>

						{showDetailModal && selectedDoc && (
							<div className='fixed inset-0 flex items-center justify-center z-50 bg-[#fffffffd] dark:bg-black bg-opacity-40'>
								<div style={{ position: 'absolute', top: 10, right: 10 }}>
									<Button type='print' pdfId='doc' fileName='Doc' tableRef={componentRef.current}>
										Print
									</Button>
								</div>

								<div className=' rounded-2xl shadow-xl p-6 w-full max-w-3xl overflow-y-auto max-h-[90vh] printable'>
									<div ref={componentRef} id='doc' className='dark:bg-gray-800 rounded-2xl  p-6 w-full max-w-3xl overflow-y-auto max-h-[90vh] printable'>
										<h2 className='text-3xl font-bold text-gray-800 mb-6 border-b pb-2 dark:text-white'>Documentation Details</h2>

										<div className='space-y-6 text-gray-700 text-base  dark:bg-gray-900 p-6 rounded-xl '>
											<div className='flex items-start gap-2'>
												<span className='w-40 font-medium text-gray-900 dark:text-white'>Title:</span>
												<span className='dark:text-white'>{selectedDoc.title || 'N/A'}</span>
											</div>

											{Array.isArray(selectedDoc.questions) && selectedDoc.questions.length > 0 ? (
												<div className='mt-4'>
													<h3 className='text-xl font-semibold mb-2 dark:text-white'>Questions</h3>
													{selectedDoc.questions.map((question: any, qIndex: number) => (
														<div key={qIndex} className='mb-4 border p-3 rounded '>
															<div className='dark:text-white'>
																<span className='font-semibold dark:text-white'>Question {qIndex + 1}:</span> {question.question || 'N/A'}
															</div>
															<div>
																<span className='font-semibold dark:text-white'>Video Link:</span>{' '}
																{question.videoLink ? (
																	<a href={question.videoLink} target='_blank' rel='noopener noreferrer' className='text-blue-600 underline'>
																		{question.videoLink}
																	</a>
																) : (
																	'N/A'
																)}
															</div>
															<div className='mt-2 ml-4'>
																<span className='font-semibold dark:text-white'>Steps:</span>
																{Array.isArray(question.steps) && question.steps.length > 0 ? (
																	<ol className='list-decimal ml-6'>
																		{question.steps.map((step: any, sIndex: number) => (
																			<li key={sIndex} className='mb-2'>
																				<div className='dark:text-white'>{step.step || 'N/A'}</div>
																				{Array.isArray(step.substeps) && step.substeps.length > 0 ? (
																					<ul className='list-disc ml-6'>
																						{step.substeps.map((substep: any, subIndex: number) => (
																							<li key={subIndex} className='mb-1'>
																								<div className='dark:text-white'>{substep.text || 'N/A'}</div>
																								{Array.isArray(substep.subpoints) && substep.subpoints.length > 0 ? (
																									<ul className='list-inside list-disc ml-6'>
																										{substep.subpoints.map((subpoint: any, spIndex: number) => (
																											<li key={spIndex} className='dark:text-white'>
																												{subpoint || 'N/A'}
																											</li>
																										))}
																									</ul>
																								) : null}
																							</li>
																						))}
																					</ul>
																				) : null}
																			</li>
																		))}
																	</ol>
																) : (
																	'N/A'
																)}
															</div>
														</div>
													))}
												</div>
											) : (
												<div>No questions available.</div>
											)}
										</div>
									</div>

									<div className='mt-6 flex justify-end gap-4'>
										<Button
											type='edit'
											onClick={() => {
												console.log('Editing selectedDoc:', selectedDoc);
												setFormData(
													JSON.parse(
														JSON.stringify({
															...selectedDoc,
															questions: Array.isArray(selectedDoc.questions)
																? selectedDoc.questions
																: [
																	{
																		question: '',
																		videoLink: '',
																		steps: [
																			{
																				step: '',
																				substeps: [
																					{
																						text: '',
																						subpoints: [],
																					},
																				],
																			},
																		],
																	},
																],
														}),
													),
												);
												setShowDetailModal(false);
												setShowForm(true);
											}}>
											Edit
										</Button>
										<Button type='close' onClick={() => setShowDetailModal(false)}>
											Close
										</Button>
									</div>
								</div>
							</div>
						)}
					</div>
				)}
			</Body>
			{showDeleteModal && docToDelete && (
				<div className='fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40'>
					<div className='bg-white rounded-xl p-6 shadow-lg max-w-md w-full'>
						<h3 className='text-lg font-semibold mb-4'>Confirm Delete</h3>
						<p>Are you sure you want to delete this documentation?</p>
						<div className='mt-6 flex justify-end gap-4'>
							<Button
								type='close'
								onClick={() => {
									setShowDeleteModal(false);
									setDocToDelete(null);
								}}>
								Cancel
							</Button>
							<Button
								type='delete'
								onClick={async () => {
									if (docToDelete) {
										await handleDelete(docToDelete);
										setShowDeleteModal(false);
										setDocToDelete(null);
									}
								}}>
								Delete
							</Button>
						</div>
					</div>
				</div>
			)}
		</Main>
	);
};

export default Documentation;
